import { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type {
	AgentManagedExtensionAttributes,
	AgentManagedExtensionParameters,
	AgentManagedObjectAttribute,
} from '../../common/agentManagedExtensionPluginType';
import {
	findAgentManagedExtension,
	findAgentManagedExtensionByLocalId,
	getNodeAiManagedObject,
} from '../../common/utils';
import { setMeta, type AgentManagedExtensionMeta } from '../pm-plugins/state';

export type AgentManagedExtensionNodeWithPos = {
	node: PMNode;
	pos: number;
};

export const getAiManagedObjectParameter = ({
	config,
	id,
}: AiManagedObject): AgentManagedObjectAttribute => ({
	configVersion: String(config.version ?? ''),
	id,
});

export const dispatchAgentManagedExtensionMeta = (
	editorView: EditorView,
	meta: AgentManagedExtensionMeta,
): void => {
	editorView.dispatch(setAgentManagedExtensionMeta(editorView.state.tr, meta));
};

export const setAgentManagedExtensionMeta = (
	tr: Transaction,
	meta: AgentManagedExtensionMeta,
): Transaction => {
	setMeta(tr, meta);
	tr.setMeta('addToHistory', false);
	return tr;
};

export const findConfiguredAgentManagedExtension = (
	editorView: EditorView,
	configLocalId?: string,
): AgentManagedExtensionNodeWithPos | undefined =>
	(configLocalId
		? findAgentManagedExtensionByLocalId(editorView.state, configLocalId)
		: undefined) ?? findAgentManagedExtension(editorView.state);

export const updateNodeAiManagedObject = (
	editorView: EditorView,
	aimo: AiManagedObject,
	localId?: string,
): void => {
	const tr = editorView.state.tr;
	if (setNodeAiManagedObject(editorView.state, tr, aimo, localId)) {
		editorView.dispatch(tr);
	}
};

export const setNodeAiManagedObject = (
	editorState: EditorState,
	tr: Transaction,
	aimo: AiManagedObject,
	localId?: string,
): boolean => {
	const currentFound =
		findAgentManagedExtensionByLocalId(editorState, localId) ??
		findAgentManagedExtension(editorState);
	if (!currentFound) {
		return false;
	}

	const { node, pos } = currentFound;
	const existingAttrs = node.attrs as AgentManagedExtensionAttributes;
	const existingAiManagedObject = getNodeAiManagedObject(existingAttrs);
	const aiManagedObject = getAiManagedObjectParameter(aimo);
	if (
		existingAiManagedObject?.id === aiManagedObject.id &&
		existingAiManagedObject.configVersion === aiManagedObject.configVersion
	) {
		return false;
	}

	const parameters = (existingAttrs.parameters ?? {}) as AgentManagedExtensionParameters;
	tr.setNodeMarkup(pos, undefined, {
		...node.attrs,
		parameters: {
			...parameters,
			macroParams: {
				...parameters.macroParams,
				aiManagedObject: {
					// Confluence macro params persist scalar values reliably. Keep
					// the public node metadata as an object by parsing on read.
					value: JSON.stringify(aiManagedObject),
				},
			},
		},
	});
	tr.setMeta('addToHistory', false);
	return true;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getParsedBodyAdf = (bodyAdf: unknown): unknown => {
	if (typeof bodyAdf !== 'string') {
		return bodyAdf;
	}

	try {
		return JSON.parse(bodyAdf);
	} catch {
		return undefined;
	}
};

const getBodyContentFragment = (
	editorState: EditorState,
	bodyAdf: unknown,
): Fragment | undefined => {
	const { schema } = editorState;
	const parsedBodyAdf = getParsedBodyAdf(bodyAdf);
	let contentAdf: unknown[];

	if (Array.isArray(parsedBodyAdf)) {
		contentAdf = parsedBodyAdf;
	} else if (
		isRecord(parsedBodyAdf) &&
		parsedBodyAdf.type === 'doc' &&
		Array.isArray(parsedBodyAdf.content)
	) {
		contentAdf = parsedBodyAdf.content;
	} else if (isRecord(parsedBodyAdf)) {
		contentAdf = [parsedBodyAdf];
	} else {
		return undefined;
	}

	const nodes = contentAdf.map((nodeAdf) => PMNode.fromJSON(schema, nodeAdf));
	nodes.forEach((node) => node.check());

	return Fragment.fromArray(nodes.length > 0 ? nodes : [schema.nodes.paragraph.createChecked()]);
};

export const replaceNodeBodyContent = (
	editorState: EditorState,
	tr: Transaction,
	bodyAdf: unknown,
	localId?: string,
): boolean => {
	const currentFound =
		findAgentManagedExtensionByLocalId(editorState, localId) ??
		findAgentManagedExtension(editorState);
	if (!currentFound) {
		return false;
	}

	const bodyContent = getBodyContentFragment(editorState, bodyAdf);
	if (!bodyContent) {
		return false;
	}

	const { node, pos } = currentFound;
	tr.replaceWith(pos + 1, pos + node.nodeSize - 1, bodyContent);
	return true;
};
